import { captureEvent } from './captureEvent'

export const DEFAULT_API_PERFORMANCE_THRESHOLDS_MS = [5000]

/**
 * Returns thresholds api most go over to log metric
 * VITE_API_PERFORMANCE_THRESHOLD_MS: one value (`5000`) or a list
 * (`3000,5000,10000`, or `[3000, 5000]`), in whole ms. Each one gets its own
 * `api-call-above-<threshold>` log. Invalid values are dropped
 * @returns {number[]} sorted, without duplicates
 */
export const getApiPerformanceThresholdsMs = () => {
  // @ts-ignore
  const raw = String(import.meta.env.VITE_API_PERFORMANCE_THRESHOLD_MS ?? '')

  const thresholds = raw
    .split(/[\s,[\]]+/)
    .filter(Boolean)
    .map((value) => Math.round(Number(value)))
    .filter((ms) => Number.isFinite(ms) && ms > 0)

  return thresholds.length
    ? [...new Set(thresholds)].sort((a, b) => a - b)
    : DEFAULT_API_PERFORMANCE_THRESHOLDS_MS
}

/**
 * Our own APIs. turbodebt.com and the current origin also serve pages and
 * static files, so only their /api/ paths count
 * @returns {RegExp[]}
 */
export const getApiUrlPatterns = () => [
  /^https:\/\/(?:.+\.)?trbotrkr\.com\//,
  /^https:\/\/(?:.+\.)?turbodevhub\.com\//,
  /^https:\/\/(?:.+\.)?turbodebt\.com\/api\//,
  new RegExp(
    `^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/api/`,
  ),
]

const API_INITIATORS = ['fetch', 'xmlhttprequest']

/**
 * Logs a finished request as `api-call-above-<threshold>` for every threshold
 *
 * @param {PerformanceResourceTiming} entry
 * @param {number[]} thresholds - ms
 * @param {RegExp[]} patterns - which URLs are API calls
 */
export function captureIfSlowApiCall(entry, thresholds, patterns) {
  const reached = thresholds.filter((threshold) => entry.duration >= threshold)

  if (
    !reached.length ||
    !API_INITIATORS.includes(entry.initiatorType) ||
    !patterns.some((pattern) => pattern.test(entry.name))
  ) {
    return
  }

  const url = new URL(entry.name)

  reached.forEach((threshold) => {
    captureEvent(`api-call-above-${threshold}`, {
      endpoint: url.pathname,
      host: url.host,
      duration_ms: Math.round(entry.duration),
      threshold_ms: threshold,
      // only some browsers expose the status of a resource entry
      ...(entry.responseStatus ? { status: entry.responseStatus } : {}),
    })
  })
}

/**
 * Watches every request the page makes and logs our slow API calls, so no
 * call site has to time itself. Requests made before this runs are included
 */
export function observeSlowApiCalls() {
  if (
    typeof PerformanceObserver === 'undefined' ||
    !PerformanceObserver.supportedEntryTypes?.includes('resource')
  ) {
    return
  }

  const thresholds = getApiPerformanceThresholdsMs()
  const patterns = getApiUrlPatterns()

  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        captureIfSlowApiCall(
          /** @type {PerformanceResourceTiming} */ (entry),
          thresholds,
          patterns,
        )
      })
    }).observe({ type: 'resource', buffered: true })
  } catch (error) {
    console.warn('Slow API call tracking failed to start', error)
  }
}
